<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ isEdit ? 'Modifier l\'hypothèse' : 'Nouvelle hypothèse' }}</h2>
        <button class="modal-close" @click="handleClose">×</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-group">
          <label for="name">Nom *</label>
          <input
            id="name"
            v-model="formData.name"
            type="text"
            required
            placeholder="Nom de l'hypothèse"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="comment">Commentaire</label>
          <textarea
            id="comment"
            v-model="formData.comment"
            placeholder="Description ou notes sur cette hypothèse..."
            rows="4"
            class="form-textarea"
          />
        </div>

        <div class="modal-footer">
          <button type="button" @click="handleClose" class="btn-cancel">
            Annuler
          </button>
          <button type="submit" :disabled="!formData.name.trim() || loading" class="btn-submit">
            {{ loading ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useHypothesesStore } from '@stores/hypotheses.store';
import { useToast } from '../composables/useToast';
import type { Hypothesis } from '@api/hypotheses.api';

const props = defineProps<{
  show: boolean;
  hypothesis?: Hypothesis | null;
}>();

const emit = defineEmits<{
  close: [];
  created: [hypothesis: Hypothesis];
  updated: [hypothesis: Hypothesis];
}>();

const hypothesesStore = useHypothesesStore();
const { success, error } = useToast();

const formData = ref({
  name: '',
  comment: '',
});

const loading = ref(false);

const isEdit = computed(() => !!props.hypothesis);

watch(
  () => props.hypothesis,
  (hypothesis) => {
    if (hypothesis) {
      formData.value = {
        name: hypothesis.name,
        comment: hypothesis.comment || '',
      };
    } else {
      formData.value = {
        name: '',
        comment: '',
      };
    }
  },
  { immediate: true }
);

watch(
  () => props.show,
  (show) => {
    if (!show) {
      formData.value = {
        name: '',
        comment: '',
      };
    }
  }
);

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (!formData.value.name.trim()) return;

  loading.value = true;
  try {
    if (isEdit.value && props.hypothesis) {
      const updated = await hypothesesStore.update(props.hypothesis.id, {
        name: formData.value.name,
        comment: formData.value.comment || undefined,
      });
      success('Hypothèse modifiée avec succès');
      emit('updated', updated);
    } else {
      const created = await hypothesesStore.create({
        name: formData.value.name,
        comment: formData.value.comment || undefined,
      });
      success('Hypothèse créée avec succès');
      emit('created', created);
    }
    handleClose();
  } catch (err: any) {
    error(err.message || 'Erreur lors de l\'enregistrement');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #e0e0e0;
}

.modal-close {
  background: none;
  border: none;
  color: #999;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #e0e0e0;
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.75rem;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #4a9eff;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #444;
}

.btn-cancel,
.btn-submit {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-cancel {
  background-color: #444;
  color: #e0e0e0;
}

.btn-cancel:hover {
  background-color: #555;
}

.btn-submit {
  background-color: #4a9eff;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background-color: #3a8eef;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
